import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Calendar } from "lucide-react";
import { toast } from "sonner";
import WarpField from "@/components/WarpField";
import { ChromeBar } from "@/components/Chrome";
import { useConfig } from "@/context/ConfigContext";
import api from "@/lib/api";

const ROLES = [
  { id: "fighter", label: "Fighter / Athlete", desc: "I compete or train and need cinematic capture." },
  { id: "creator", label: "Creator / Brand", desc: "I run a brand, podcast, or platform in the culture." },
  { id: "client", label: "Client / Production", desc: "I'm booking JITSUENT for a project, film, or event." },
];

const TIMELINES = ["This week", "1–2 weeks", "1–3 months", "3+ months", "Ongoing"];
const BUDGETS = ["Under $2k", "$2k – $5k", "$5k – $15k", "$15k – $50k", "$50k+", "Let's discuss"];

const STEPS = [
  "WELCOME",
  "WHO ARE YOU",
  "IDENTITY",
  "PROJECT",
  "VISION",
  "LOGISTICS",
  "TRANSMIT",
];

export default function Onboarding() {
  const cfg = useConfig();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    role: "",
    name: "",
    email: "",
    phone: "",
    location: "",
    socials: "",
    project_type: "",
    vision: "",
    timeline: "",
    budget: "",
    referral: "",
  });

  const update = (k, v) => setForm({ ...form, [k]: v });

  const canNext = () => {
    if (step === 1) return !!form.role;
    if (step === 2) return form.name.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
    if (step === 3) return form.project_type.trim();
    if (step === 4) return form.vision.trim();
    if (step === 5) return form.timeline && form.budget;
    return true;
  };

  // Load Calendly script when done & we have URL
  useEffect(() => {
    if (!done || !cfg.calendly_url) return;
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.body.appendChild(script);
    const link = document.createElement("link");
    link.href = "https://assets.calendly.com/assets/external/widget.css";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    return () => {
      try { document.body.removeChild(script); } catch (e) {}
      try { document.head.removeChild(link); } catch (e) {}
    };
  }, [done, cfg.calendly_url]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/onboarding", form);
      setDone(true);
      toast.success("Welcome to the family.");
    } catch {
      toast.error("Transmission failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-black min-h-screen relative overflow-hidden" data-testid="onboarding-page">
      <div className="absolute inset-0 opacity-40 pointer-events-none">
        <WarpField density={120} speed={0.5} />
      </div>
      <div className="absolute inset-0 vignette pointer-events-none" />

      <div className="relative max-w-3xl mx-auto px-6 pt-32 pb-32">
        {/* HUD */}
        <div className="flex items-center justify-between font-mono text-[10px] tracking-[0.3em] text-neutral-500 mb-12">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-white rounded-full flicker" />
            JITSUENT // MISSION BRIEF // v1
          </div>
          <div>STEP {String(step + 1).padStart(2, "0")} / {String(STEPS.length).padStart(2, "0")}</div>
        </div>

        {/* Progress */}
        <div className="grid grid-cols-7 gap-1 mb-16" data-testid="onboarding-progress">
          {STEPS.map((s, i) => (
            <div key={s} className="h-px bg-neutral-900 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: i <= step ? "100%" : 0 }}
                transition={{ duration: 0.5 }}
                className="h-full bg-white"
              />
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {done ? (
            <motion.div
              key="done"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="py-12"
              data-testid="onboarding-done"
            >
              <div className="text-center">
                <ChromeBar className="w-24 mx-auto mb-8" />
                <div className="font-mono text-[10px] text-neutral-500 tracking-widest mb-4">SIGNAL RECEIVED</div>
                <h2 className="font-bodoni font-black text-6xl md:text-7xl leading-[0.9] mb-6">
                  Welcome to <span className="italic">the family</span>.
                </h2>
                <p className="font-mono text-sm text-neutral-400 max-w-md mx-auto">
                  Your transmission has been logged. We'll roll back within 48 hours.
                </p>
              </div>

              {cfg.calendly_url && (
                <div className="mt-12 border border-neutral-900 p-4" data-testid="onboarding-calendly">
                  <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-neutral-500 mb-4 px-2">
                    <Calendar size={12} /> LOCK IN A SESSION — OPTIONAL
                  </div>
                  <div
                    className="calendly-inline-widget"
                    data-url={`${cfg.calendly_url}?background_color=000000&text_color=ffffff&primary_color=ffffff&hide_event_type_details=0&hide_gdpr_banner=1&name=${encodeURIComponent(form.name)}&email=${encodeURIComponent(form.email)}`}
                    style={{ minWidth: "320px", height: "700px" }}
                  />
                </div>
              )}

              <div className="mt-10 font-mono text-[10px] text-neutral-700 tracking-[0.3em] text-center">
                JOINT OSS · ROLL READY · 4EVER ROLLIN'
              </div>
            </motion.div>
          ) : (
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              {step === 0 && (
                <div className="text-center py-12" data-testid="step-welcome">
                  <div className="font-mono text-[10px] text-neutral-500 tracking-widest mb-6 cursor-blink">INITIALIZING TRANSMISSION</div>
                  <h1 className="font-bodoni text-6xl md:text-8xl leading-[0.9] mb-6">
                    Welcome to <span className="italic">the Family</span>.
                  </h1>
                  <p className="font-mono text-sm text-neutral-400 max-w-lg mx-auto">
                    Let's get you set up. This will only take a few minutes and help us
                    create something amazing together.
                  </p>
                </div>
              )}

              {step === 1 && (
                <div data-testid="step-role">
                  <Label code="01" title="Who are you?" sub="Pick the path that fits." />
                  <div className="grid grid-cols-1 gap-3 mt-8">
                    {ROLES.map((r) => (
                      <button
                        key={r.id}
                        onClick={() => update("role", r.id)}
                        data-testid={`role-${r.id}`}
                        className={`text-left p-6 border transition-all ${
                          form.role === r.id ? "border-white bg-neutral-950" : "border-neutral-900 hover:border-neutral-600"
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="font-bodoni text-2xl mb-1">{r.label}</div>
                            <div className="font-mono text-xs text-neutral-500">{r.desc}</div>
                          </div>
                          {form.role === r.id && <Check size={20} className="text-white" />}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div data-testid="step-identity">
                  <Label code="02" title="Identify yourself." sub="So we can reach back." />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
                    <Input label="Full Name" testid="ob-name" value={form.name} onChange={(v) => update("name", v)} />
                    <Input label="Email" type="email" testid="ob-email" value={form.email} onChange={(v) => update("email", v)} />
                    <Input label="Phone" testid="ob-phone" value={form.phone} onChange={(v) => update("phone", v)} />
                    <Input label="Location" testid="ob-location" value={form.location} onChange={(v) => update("location", v)} />
                    <div className="md:col-span-2">
                      <Input label="Socials / Handles" testid="ob-socials" value={form.socials} onChange={(v) => update("socials", v)} placeholder="@instagram · @tiktok · website" />
                    </div>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div data-testid="step-project">
                  <Label code="03" title="What are we capturing?" sub="One line on the project type." />
                  <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {["Highlight Reel", "Walk-Out Film", "Brand Campaign", "Documentary", "Photo Editorial", "Event Coverage", "Other"].map((p) => (
                      <button
                        key={p}
                        onClick={() => update("project_type", p)}
                        data-testid={`project-${p.replace(/\s/g, "")}`}
                        className={`text-left p-4 border font-mono text-sm transition-all ${
                          form.project_type === p ? "border-white bg-neutral-950 text-white" : "border-neutral-900 text-neutral-400 hover:border-neutral-600"
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                  <div className="mt-4">
                    <Input
                      label="Or describe in your words"
                      testid="ob-projecttype-custom"
                      value={form.project_type}
                      onChange={(v) => update("project_type", v)}
                    />
                  </div>
                </div>
              )}

              {step === 4 && (
                <div data-testid="step-vision">
                  <Label code="04" title="What's the vision?" sub="Tone, references, dreams. Speak freely." />
                  <div className="mt-8">
                    <textarea
                      data-testid="ob-vision"
                      value={form.vision}
                      onChange={(e) => update("vision", e.target.value)}
                      rows={8}
                      className="noir-input"
                      placeholder="Spaceballs ludicrous speed meets Akira Kurosawa. Roll forever."
                    />
                  </div>
                </div>
              )}

              {step === 5 && (
                <div data-testid="step-logistics">
                  <Label code="05" title="Logistics." sub="Timeline and resources." />
                  <div className="mt-8 space-y-8">
                    <div>
                      <div className="font-mono text-[10px] text-neutral-500 tracking-widest mb-3">TIMELINE</div>
                      <div className="flex flex-wrap gap-2">
                        {TIMELINES.map((t) => (
                          <button
                            key={t}
                            onClick={() => update("timeline", t)}
                            data-testid={`timeline-${t}`}
                            className={`px-4 py-2 border font-mono text-xs transition-all ${
                              form.timeline === t ? "border-white bg-white text-black" : "border-neutral-900 text-neutral-400 hover:border-neutral-600"
                            }`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="font-mono text-[10px] text-neutral-500 tracking-widest mb-3">BUDGET</div>
                      <div className="flex flex-wrap gap-2">
                        {BUDGETS.map((b) => (
                          <button
                            key={b}
                            onClick={() => update("budget", b)}
                            data-testid={`budget-${b}`}
                            className={`px-4 py-2 border font-mono text-xs transition-all ${
                              form.budget === b ? "border-white bg-white text-black" : "border-neutral-900 text-neutral-400 hover:border-neutral-600"
                            }`}
                          >
                            {b}
                          </button>
                        ))}
                      </div>
                    </div>
                    <Input label="How'd you find us?" testid="ob-referral" value={form.referral} onChange={(v) => update("referral", v)} />
                  </div>
                </div>
              )}

              {step === 6 && (
                <div data-testid="step-confirm">
                  <Label code="06" title="Ready to transmit?" sub="Review then send." />
                  <div className="mt-8 border border-neutral-900 divide-y divide-neutral-900 font-mono text-xs">
                    {[
                      ["ROLE", form.role],
                      ["NAME", form.name],
                      ["EMAIL", form.email],
                      ["PHONE", form.phone || "—"],
                      ["LOCATION", form.location || "—"],
                      ["SOCIALS", form.socials || "—"],
                      ["PROJECT", form.project_type],
                      ["VISION", form.vision],
                      ["TIMELINE", form.timeline],
                      ["BUDGET", form.budget],
                      ["REFERRAL", form.referral || "—"],
                    ].map(([k, v]) => (
                      <div key={k} className="grid grid-cols-3 p-4 gap-4">
                        <div className="text-neutral-600 tracking-widest">{k}</div>
                        <div className="col-span-2 text-white break-words">{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {!done && (
          <div className="mt-12 flex justify-between items-center">
            <button
              data-testid="ob-prev"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              className="btn-ghost"
            >
              <ArrowLeft size={14} /> Back
            </button>
            {step < STEPS.length - 1 ? (
              <button
                data-testid="ob-next"
                onClick={() => setStep((s) => s + 1)}
                disabled={!canNext()}
                className="btn-noir"
              >
                {step === 0 ? "Begin Transmission" : "Continue"} <ArrowRight size={14} />
              </button>
            ) : (
              <button
                data-testid="ob-submit"
                onClick={submit}
                disabled={busy}
                className="btn-noir"
              >
                {busy ? "TRANSMITTING..." : "Send →"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Label({ code, title, sub }) {
  return (
    <div>
      <div className="font-mono text-[10px] text-neutral-500 tracking-widest mb-3">{code} / {sub}</div>
      <h2 className="font-bodoni text-5xl md:text-6xl leading-[0.95]">{title}</h2>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] text-neutral-500 tracking-widest mb-2">{label.toUpperCase()}</div>
      <input
        data-testid={testid}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="noir-input"
      />
    </label>
  );
}
